import React from 'react';

interface FormFieldProps {
  label: string;
  htmlFor: string;
  error?: string;
  helper?: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * FormField Component - Clinic Studios Design System
 *
 * Wrapper for form inputs that provides:
 * - Accessible label association
 * - Error state styling and message
 * - Helper text
 * - Required indicator
 */
const FormField: React.FC<FormFieldProps> = ({
  label,
  htmlFor,
  error,
  helper,
  required = false,
  children,
  className = '',
}) => {
  return (
    <div className={`mb-4 ${className}`}>
      <label
        htmlFor={htmlFor}
        className="block text-sm font-medium text-clinic-dark mb-1.5 font-body"
      >
        {label}
        {required && <span className="text-clinic-accent ml-1">*</span>}
      </label>
      {children}
      {error && (
        <p className="mt-1.5 text-sm text-clinic-accent flex items-center gap-1">
          <svg
            className="w-4 h-4 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </p>
      )}
      {helper && !error && (
        <p className="mt-1.5 text-sm text-clinic-muted">{helper}</p>
      )}
    </div>
  );
};

// Input Component
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Input: React.FC<InputProps> = ({
  error = false,
  leftIcon,
  rightIcon,
  className = '',
  ...props
}) => {
  const baseStyles = `
    w-full px-4 py-2.5
    bg-white
    border rounded-input
    text-clinic-dark text-base font-body
    placeholder:text-clinic-muted
    transition-colors duration-150
    focus:outline-none focus:ring-2 focus:ring-offset-0
    disabled:bg-clinic-surface-sunken disabled:text-clinic-muted disabled:cursor-not-allowed
  `;

  const stateStyles = error
    ? 'border-clinic-accent focus:border-clinic-accent focus:ring-clinic-accent/20'
    : 'border-clinic-primary-light focus:border-clinic-primary focus:ring-clinic-primary/20';

  const iconPadding = leftIcon ? 'pl-11' : rightIcon ? 'pr-11' : '';

  return (
    <div className="relative">
      {leftIcon && (
        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-clinic-muted pointer-events-none">
          {leftIcon}
        </div>
      )}
      <input
        className={`${baseStyles} ${stateStyles} ${iconPadding} ${className}`.replace(/\s+/g, ' ').trim()}
        {...props}
      />
      {rightIcon && (
        <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-clinic-muted">
          {rightIcon}
        </div>
      )}
    </div>
  );
};

// Textarea Component
interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
}

export const Textarea: React.FC<TextareaProps> = ({
  error = false,
  className = '',
  ...props
}) => {
  const baseStyles = `
    w-full px-4 py-2.5
    bg-white
    border rounded-input
    text-clinic-dark text-base font-body
    placeholder:text-clinic-muted
    transition-colors duration-150
    focus:outline-none focus:ring-2 focus:ring-offset-0
    disabled:bg-clinic-surface-sunken disabled:text-clinic-muted disabled:cursor-not-allowed
    resize-y min-h-[100px]
  `;

  const stateStyles = error
    ? 'border-clinic-accent focus:border-clinic-accent focus:ring-clinic-accent/20'
    : 'border-clinic-primary-light focus:border-clinic-primary focus:ring-clinic-primary/20';

  return (
    <textarea
      className={`${baseStyles} ${stateStyles} ${className}`.replace(/\s+/g, ' ').trim()}
      {...props}
    />
  );
};

// Select Component
interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  error?: boolean;
  options: { value: string; label: string }[];
  placeholder?: string;
}

export const Select: React.FC<SelectProps> = ({
  error = false,
  options,
  placeholder,
  className = '',
  ...props
}) => {
  const baseStyles = `
    w-full px-4 py-2.5 pr-10
    bg-white
    border rounded-input
    text-clinic-dark text-base font-body
    transition-colors duration-150
    focus:outline-none focus:ring-2 focus:ring-offset-0
    disabled:bg-clinic-surface-sunken disabled:text-clinic-muted disabled:cursor-not-allowed
    appearance-none cursor-pointer
  `;

  const stateStyles = error
    ? 'border-clinic-accent focus:border-clinic-accent focus:ring-clinic-accent/20'
    : 'border-clinic-primary-light focus:border-clinic-primary focus:ring-clinic-primary/20';

  return (
    <div className="relative">
      <select
        className={`${baseStyles} ${stateStyles} ${className}`.replace(/\s+/g, ' ').trim()}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-clinic-muted">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
};

// Checkbox Component
interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: React.ReactNode;
  description?: string;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  description,
  className = '',
  id,
  ...props
}) => {
  return (
    <div className={`flex items-start ${className}`}>
      <div className="flex items-center h-5">
        <input
          id={id}
          type="checkbox"
          className="
            h-4 w-4 rounded
            text-clinic-primary
            border-clinic-primary-light
            focus:ring-clinic-primary focus:ring-offset-0
            cursor-pointer
            disabled:cursor-not-allowed disabled:opacity-50
          "
          {...props}
        />
      </div>
      <div className="ml-3">
        <label
          htmlFor={id}
          className="text-sm font-medium text-clinic-dark cursor-pointer"
        >
          {label}
        </label>
        {description && (
          <p className="text-sm text-clinic-muted mt-0.5">{description}</p>
        )}
      </div>
    </div>
  );
};

export default FormField;
